import { type RefObject, useCallback, useEffect, useState } from 'react';

import { AsyncEffect } from '@schibsted-nmp/advertising-companion';
import { $config, $initialState } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { getAllFeatureStatuses } from '@client/core/atoms/unleashFeatures.js';
import { useInScreen } from '@client/core/hooks/index.js';
import { i18n } from '@lingui/core';
import {
  forceBatchRequestPlacements,
  type InViewSettings
} from '@client/core/utils/getInViewSettingsByPlacementId.js';
import {
  AdPlacement,
  type ClientAdPlacement,
  debugLog,
  DisplayPlacementId,
  type GamPlacement,
  getSizesByMediaType,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';
import { getExperimentTargetingForPlacement } from '@client/core/utils/amplitude/index.js';

import { BatchedSlots } from '../batch.js';
import { setTargetingOnSlotOrGlobal } from '../targeting.js';
import { applySizeMapping } from '../utils/sizeMapping.js';
import type { GamAdUnitProps } from './GamAdUnit.js';
import { ensurePathStartsWithSlash, shouldUseSplitBatch } from './utils.js';
import { Api } from '../Api/index.js';

export function useInitiateGamUnit(
  props: GamAdUnitProps & {
    ref: RefObject<HTMLDivElement>;
    isGamLazyLoadingEnabled: boolean;
  }
) {
  const [placementIsInView, setPlacementIsInView] = useState(false);

  const { adServer } = $config.get() ?? {};
  const gamConfig = adServer?.gam;
  const { placement, inViewSettings, isGamLazyLoadingEnabled } = props;
  const { placementId, status } = placement;

  const { isIntersecting } = useShouldLoadAd({
    placementId,
    ref: props.ref,
    viewSettings: inViewSettings,
    isGamLazyLoadingEnabled
  });

  const [slotState, setSlotState] = useState(() =>
    getGamSlotOrNull.sync(placementId)
  );

  /** Loads the ads in the current batch. */
  const sendToRequestBatchAds = useCallback((slot: googletag.Slot | null) => {
    // Disable split batch request on FINN Travel Result and Object pages to prevent race conditions
    if (slot && !shouldUseSplitBatch()) {
      BatchedSlots.Queue.add(slot);
    } else if (slot) {
      BatchedSlots.SplitBatchQueue.add(slot);
    }
  }, []);

  // Some placements need to wait for a delay before they are in view, because
  // of pagination trouble etc
  useEffect(() => {
    if (status !== 'refresh') {
      setPlacementIsInView(isIntersecting);
      return;
    }

    // BatchedSlots.IMMEDIATE_SLOTS should be set to pending immediately when in refresh status
    if (
      BatchedSlots.IMMEDIATE_SLOTS.includes(
        placementId as DisplayPlacementId
      ) &&
      !shouldUseSplitBatch()
    ) {
      updatePlacementKeyValueById(placementId, 'status', 'pending');
      setPlacementIsInView(true); // Force it to be considered "in view"
      return;
    }

    const placementsToWaitFor = new Array<PlacementId>(
      AdPlacement.AdvtBottom1.id
    );

    if (!placementsToWaitFor.includes(placementId)) {
      updatePlacementKeyValueById(placementId, 'status', 'pending');
      setPlacementIsInView(isIntersecting);
      return;
    }

    if (isIntersecting) return;

    // As soon as the ad is not intersecting anymore, we will stage it to be ready to load (pending)
    updatePlacementKeyValueById(placementId, 'status', 'pending');
    setPlacementIsInView(isIntersecting);
  }, [isIntersecting, status, placementId]);

  useEffect(() => {
    // Disable split batch request on FINN Travel Result and Object pages to prevent race conditions
    if (!shouldUseSplitBatch()) {
      // forceBatchRequest is a special case where we want to load the ads in a batch, such as the horseshoe placements (left,right,top,wallpaper)
      // If that happens, completely ignore inView and just send them straight to request
      const isPendingInViewOrForceBatch =
        (placementIsInView && status === 'pending') ||
        status === 'forceBatchRequest';

      if (slotState && isPendingInViewOrForceBatch) {
        updatePlacementKeyValueById(placementId, 'status', 'request');
        sendToRequestBatchAds(slotState);
      }
    } else if (
      slotState &&
      placementIsInView &&
      (status === 'pending' || status === 'forceBatchRequest')
    ) {
      updatePlacementKeyValueById(placementId, 'status', 'request');
      sendToRequestBatchAds(slotState);
    }
  }, [slotState, placementIsInView, placementId, status]);

  // Set up the ad:
  AsyncEffect.use(() => {
    if (slotState) return {};

    return {
      async effect({ abortSignal }) {
        try {
          if (await getGamSlotOrNull.async(placementId, abortSignal)) return; // Slot already exists

          debugLog('Defining slot for placement', placementId);

          const iframeTranslation = i18n.t({
            id: 'native.advertisement',
            message: 'advertisement'
          });

          // Set title before the slot definition
          googletag.setAdIframeTitle(iframeTranslation);

          const slot = await getSlot(placement, abortSignal);

          setSlotState(slot);
          updateMetricByKey(placementId, PlacementStatus.SlotCreated);
          debugLog('GAM successfully set up for placement', placementId);
        } catch (error) {
          debugLog(`Failed setting up GAM for placement ${placementId}`, error);

          if (!(error instanceof DOMException && error.name === 'AbortError')) {
            // If the error is not an AbortError, we rethrow it so that it gets tracked:
            throw error;
          }
        }
      }
    };
  }, [placement, gamConfig, placementId, slotState]);

  return { placement, isIntersecting };
}

async function getSlot(
  placement: ClientAdPlacement<GamPlacement>,
  ...abortSignals: ReadonlyArray<AbortSignal>
) {
  const {
    targeting = [],
    path = '',
    sizeMappings,
    mediaTypes = []
  } = placement.adServer.config;

  const sizes = Array.from(getSizesByMediaType(mediaTypes));
  const placementConfigPath = ensurePathStartsWithSlash(path);
  const { enableGamTestCampaign, enableTop1Wallpaper } =
    getAllFeatureStatuses();

  if (
    enableTop1Wallpaper &&
    placement.placementId === DisplayPlacementId.AdvtTop1
  ) {
    // tmp until tested on blocket and the placements is added to the backend
    sizes.push([1600, 900]);
  }

  const slot = await Api(...abortSignals)
    .defineSlot(placementConfigPath, sizes, placement.placementId)
    .addService(googletag.pubads());

  // Apply size mapping if available
  if (sizeMappings && sizeMappings.length > 0) {
    debugLog(
      `Applying size mapping for placement ${placement.placementId}:`,
      sizeMappings
    );
    await applySizeMapping(slot, sizeMappings, mediaTypes, ...abortSignals);
  }

  if ($initialState.get().env === 'local') {
    targeting.push({ key: 'test', value: ['true'] });
  }

  /*
    Setting 'nmp_placement' to an empty string is necessary for Relevant Yield
    to be able to match the GAM ad unit with the correct placement in the
    Relevant Yield configuration for DBA. This is part of a legendary setup that's
    necessary for DBA, and we can't change it since the ad server config in
    Relevant Yield is shared with Bilbasen, at least until Bilbasen is on Aurora.
    Unfortunately, this is some legendary setup that spills over to Aurora, but
    adding it doesn't affect other the brands, so we can safely set it without
    any considerations for which brand it is:
  */
  slot.setTargeting('nmp_placement', ['']);

  if (enableGamTestCampaign) {
    targeting.push({ key: 'gamTestCampaign', value: ['true'] });
  }

  // Check for placement-specific experiment targeting
  const experimentTargeting = getExperimentTargetingForPlacement(
    placement.placementId
  );

  if (experimentTargeting) {
    debugLog(
      `Applying experiment targeting to placement ${placement.placementId}:`,
      experimentTargeting
    );

    slot.setTargeting(experimentTargeting.key, experimentTargeting.value);
  }

  if (targeting.length > 0) {
    await setTargetingOnSlotOrGlobal({ slot, targeting }, ...abortSignals);
  }

  return slot;
}

function getGamSlotOrNull(placementId: PlacementId, googletag: Api.Loaded) {
  return (
    googletag
      .pubads()
      .getSlots()
      .find((slot) => slot.getSlotElementId() === String(placementId)) ?? null
  );
}

namespace getGamSlotOrNull {
  export async function async(
    placementId: PlacementId,
    ...abortSignals: ReadonlyArray<AbortSignal>
  ) {
    return getGamSlotOrNull.sync(placementId, await Api(...abortSignals));
  }

  export function sync(placementId: PlacementId, googletag?: Api.Loaded) {
    try {
      return getGamSlotOrNull(placementId, googletag ?? Api.unsafe_googletag);
    } catch {
      return null;
    }
  }
}

function useShouldLoadAd({
  placementId,
  ref,
  viewSettings,
  isGamLazyLoadingEnabled
}: {
  placementId: PlacementId;
  ref: RefObject<HTMLDivElement>;
  viewSettings: InViewSettings | undefined;
  isGamLazyLoadingEnabled: boolean;
}) {
  if (isGamLazyLoadingEnabled) {
    return {
      isIntersecting: true
    };
  }
  const inViewSettings = forceBatchRequestPlacements.includes(placementId)
    ? null
    : viewSettings;

  const { isIntersecting } = useInScreen({
    ref,
    ...(inViewSettings ?? {})
  });

  return {
    isIntersecting: isIntersecting || !inViewSettings
  };
}
